/* eslint-disable react/no-children-prop */
import { DownloadIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useReactToPrint } from 'react-to-print';

import { exportExcel } from '../../../utils/excelexport';
import { PaginationSquare } from '../pagination/PaginationSquare';

type TableSalesContentProps = {
  dataTable: {
    nome_cliente: string;
    telefone_cliente: string;
    email_cliente: string;
    cpf_cliente: string;
    causa_falha?: 'erro_processamento' | 'abandono';
    data_nascimento_cliente: string;
    nome_profissao_cliente: string;
    numero_proposta?: number;
  }[];
  hiddenColumns?: string[];
  current: number;
  limit: number;
  setLimit: any;
  total: number;
  offset: number;
  setOffset: any;
  quantitative: number;
  label: string;
  isLoading: boolean;
  dataOptionsForDataExport: {
    type: string;
  }[];
  handleSearch: (valorBusca: string, event: 'change' | 'blur') => void;
  searchBarValue: string;
};

const causaFalhaMap = {
  abandono: 'Abandono',
  erro_processamento: 'Erro no processamento'
};

export const TableSalesContent = ({
  current,
  limit,
  setLimit,
  total,
  offset,
  setOffset,
  dataTable,
  hiddenColumns = [],
  quantitative,
  label,
  dataOptionsForDataExport,
  isLoading,
  handleSearch,
  searchBarValue
}: TableSalesContentProps) => {
  const toast = useToast();

  const refPrint = useRef() as MutableRefObject<HTMLDivElement>;

  const [dataExport, setDataExport] = useState('');
  const [valueForSearch, setValueForSearch] = useState('');

  const indexCurrentData = () => {
    return dataTable?.slice(current * limit - limit, current * limit);
  };

  function removeHiddenProperties(
    obj: {
      [key: string]: any; // Assuming the object can have any kind of values
    },
    keysToRemove: string[]
  ) {
    keysToRemove.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        delete obj[key];
      }
    });

    return obj;
  }

  const csvData = () => {
    return dataTable.map((item) => {
      return removeHiddenProperties(
        {
          Nome: item.nome_cliente,
          Telefone: item.telefone_cliente,
          'E-mail': item.email_cliente,
          CPF: item.cpf_cliente,
          Proposta: item.numero_proposta,
          Motivo: item.causa_falha,
          'Data de Nascimento': item.data_nascimento_cliente,
          Profissão: item.nome_profissao_cliente
        },
        hiddenColumns
      );
    });
  };

  const csvLink = () => {
    return {
      filename: `${label}.csv`,
      data: csvData()
    };
  };

  const handlePrint = useReactToPrint({
    content: () => {
      return refPrint.current;
    }
  });

  const handleLinkForExportData = () => {
    if (dataExport === '') {
      toast({
        position: 'top',
        description: `Selecione um tipo de arquivo`,
        status: 'error',
        duration: 10000,
        isClosable: true
      });
    }
    if (dataExport === 'pdf') {
      handlePrint();
    }

    if (dataExport === 'xlsx') {
      exportExcel({
        excelData: csvData(),
        fileName: `${
          label.includes('não') ? 'vendas-nao-efetivadas' : 'vendas-efetivadas'
        }-${new Date().toISOString().substring(0, 10)}`
      });
    }
  };

  useEffect(() => {
    handleSearch(valueForSearch, 'change');
  }, [valueForSearch]);

  useEffect(() => {
    setValueForSearch(searchBarValue);
  }, [searchBarValue]);

  return (
    <Stack
      m="30px 0 0 0 !important"
      bg="#FFFFFF"
      p="30px"
      justifyContent="space-between"
      borderRadius="24px"
      boxShadow="0px 0px 5px rgba(50, 50, 50, 0.25)"
    >
      <Box display="flex !important" justifyContent="space-between">
        <Stack>
          <Text fontWeight="700" fontSize="1.25rem" lineHeight="2rem">
            {label}
          </Text>
          <Text
            fontWeight="400"
            fontSize="1rem"
            lineHeight="1.5rem"
            color="#001E64"
          >
            Quantidade de vendas: {quantitative}
          </Text>
        </Stack>
        <HStack gridGap="10px">
          <InputGroup w="250px">
            <InputLeftElement
              pointerEvents="none"
              h="50px"
              children={<SearchIcon color="#001E64 " />}
            />
            <Input
              w="250px"
              h="50px"
              value={valueForSearch}
              border="1px solid #001E64 !important"
              borderRadius="133px"
              placeholder="Busca por (produto, nome, proposta ou situação)"
              onChange={(e) => {
                setValueForSearch(e.target.value);
              }}
              onBlur={(e) => {
                handleSearch(e.target.value, 'blur');
              }}
              _hover={{}}
              _active={{}}
              _focus={{
                '&:focus': {
                  boxShadow: 'none !important'
                }
              }}
            />
          </InputGroup>
          <Select
            w="250px"
            h="50px"
            value={dataExport}
            color={dataExport === '' ? '#828282' : '#001E64'}
            fontWeight={dataExport === '' ? '400' : '700'}
            border="1px solid #001E64 !important"
            borderRadius="133px"
            placeholder="Selecione um tipo de arquivo"
            onChange={(e) => {
              setDataExport(e.target.value);
            }}
            _hover={{}}
            _active={{}}
            _focus={{
              '&:focus': {
                boxShadow: 'none !important'
              }
            }}
          >
            {dataOptionsForDataExport.map((item, index) => {
              return (
                <option key={index} value={item.type}>
                  {item.type}
                </option>
              );
            })}
          </Select>
          {dataExport === 'csv' ? (
            <CSVLink {...csvLink()} separator={';'}>
              <Tooltip
                label="Exportar dados"
                bg="gray.200"
                placement="top-end"
                color="#323232"
              >
                <DownloadIcon
                  m="0 !important"
                  w="18px"
                  h="18px"
                  color="#001E64"
                />
              </Tooltip>
            </CSVLink>
          ) : (
            <Box>
              <Tooltip
                label="Exportar dados"
                bg="gray.200"
                placement="top-end"
                color="#323232"
              >
                <DownloadIcon
                  m="0 !important"
                  w="18px"
                  h="18px"
                  color="#001E64"
                  onClick={() => {
                    handleLinkForExportData();
                  }}
                />
              </Tooltip>
            </Box>
          )}
        </HStack>
      </Box>
      {isLoading ? (
        <Box
          w="100%"
          h="55vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gridGap="15px"
        >
          <Spinner
            thickness="6px"
            speed="0.65s"
            emptyColor="#FF5000"
            color="#FFFFFF"
            size="xl"
          />
          <Text>Carregando Acessos...</Text>
        </Box>
      ) : (
        <TableContainer
          ref={refPrint}
          m="20px 0 0 0 !important"
          w="100%"
          maxH="170vh"
          overflowY="auto"
        >
          <Table
            variant="striped"
            colorScheme="whiteAlpha"
            size="sm"
            position="relative"
            style={{ borderCollapse: 'separate', borderSpacing: '0 10px' }}
          >
            <Thead
              h="3.25rem"
              z-index={9}
              position="sticky"
              top={0}
              zIndex={999}
              bg="#FFFFFF"
            >
              <Tr>
                <Th
                  whiteSpace="normal"
                  textTransform="none"
                  fontSize="0.875rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                >
                  Nome
                </Th>
                <Th
                  whiteSpace="normal"
                  textTransform="none"
                  fontSize="0.875rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                >
                  Telefone
                </Th>
                <Th
                  whiteSpace="normal"
                  textTransform="none"
                  fontSize="0.875rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                >
                  E-mail
                </Th>
                <Th
                  whiteSpace="normal"
                  textTransform="none"
                  fontSize="0.875rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                >
                  CPF
                </Th>
                {!hiddenColumns.includes('Proposta') && (
                  <Th
                    whiteSpace="normal"
                    textTransform="none"
                    fontSize="0.875rem"
                    fontWeight="700"
                    lineHeight="1.5rem"
                    color="#001E64"
                    p="20px 0 15px 15px !important"
                  >
                    Proposta
                  </Th>
                )}
                {!hiddenColumns.includes('Motivo') && (
                  <Th
                    whiteSpace="normal"
                    textTransform="none"
                    fontSize="0.875rem"
                    fontWeight="700"
                    lineHeight="1.5rem"
                    color="#001E64"
                    p="20px 0 15px 15px !important"
                  >
                    Motivo
                  </Th>
                )}
                <Th
                  whiteSpace="normal"
                  textTransform="none"
                  fontSize="0.875rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Data <br /> de Nascimento
                </Th>
                <Th
                  whiteSpace="normal"
                  textTransform="none"
                  fontSize="0.875rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                >
                  Profissão
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {console.log(indexCurrentData(), 'index current data tbsc')}
              {Array.isArray(indexCurrentData())
                ? indexCurrentData()?.map((item: any, index: any) => {
                    const date = new Date(item.data_nascimento_cliente);

                    const year = date.getFullYear();
                    const month = date.getMonth() + 1;
                    const day = date.getDate();

                    const newDate = `${day < 10 ? `0${day}` : day}/${
                      month < 10 ? `0${month}` : month
                    }/${year}`;

                    return (
                      <Tr key={index}>
                        <Td
                          display="flex"
                          alignItems="center"
                          gridGap="10px"
                          position="relative"
                          p="16px 0 16px 15px !important"
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          <Box
                            w="7px"
                            h="73.24px"
                            bg={'#FF5000'}
                            borderRadius="24px 0 0 24px"
                            position="absolute"
                            left="0"
                          >
                            <span style={{ visibility: 'hidden' }}>tag</span>
                          </Box>
                          <Box p="10px">{item.nome_cliente}</Box>
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          {item.telefone_cliente}
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          {item.email_cliente}
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          {item.cpf_cliente}
                        </Td>
                        {!hiddenColumns.includes('Proposta') && (
                          <Td
                            border="none"
                            bg="rgba(211, 222, 235, .5)"
                            color="#001E64"
                            fontSize="0.875rem"
                            lineHeight="1.5rem"
                            fontWeight="400"
                          >
                            {item.numero_proposta}
                          </Td>
                        )}
                        {!hiddenColumns.includes('Motivo') && (
                          <Td
                            border="none"
                            bg="rgba(211, 222, 235, .5)"
                            color="#001E64"
                            fontSize="0.875rem"
                            lineHeight="1.5rem"
                            fontWeight="400"
                          >
                            {causaFalhaMap[item.causa_falha]}
                          </Td>
                        )}
                        <Td
                          border="none"
                          bg="rgb(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                          textAlign="center"
                        >
                          {newDate}
                        </Td>
                        <Td
                          w="100px !important"
                          border="none"
                          bg="rgb(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          {item.nome_profissao_cliente}
                        </Td>
                      </Tr>
                    );
                  })
                : ''}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <PaginationSquare
        offset={offset}
        limit={limit}
        total={total}
        setOffset={setOffset}
        setLimit={setLimit}
      />
    </Stack>
  );
};
